import {action, computed, observable} from 'mobx';
import { observer } from 'mobx-react';
import {autoinject, inject, SubTopBar, bind, Tooltip, PopoutHandle} from 'Mri.Web.Common';
import * as React from 'react';
import { AccountStore } from '../../Account/AccountStore';
import { AppStore } from '../../Core/AppStore';
import { DisplayFilter } from '../../DisplayFilters/DisplayFilter';
import { DisplayListViewModel } from '../../DisplayFilters/DisplayListViewModel';
import { DisplayGridColumnPresets } from './DisplayGridColumnPresets';
import { GridSettings } from './GridSettings';
import { DisplayGridViewSelector, DisplayGridViewTypes } from './DisplayGridViewSelector';
import {
    BsLayoutThreeColumns,
    BsCardChecklist,
    BsGear
} from "react-icons/bs";
import {Button} from "reactstrap";
import Select from "react-select";
import {Column} from "ag-grid-enterprise";
import {BsFillFunnelFill} from "@react-icons/all-files/bs/BsFillFunnelFill";
import {GridBulkActions} from "./GridBulkActions";

export interface IDisplayGridNav {
    readonly vm: DisplayListViewModel;
    readonly hideInput?: boolean;
    readonly link?: boolean;
}

@autoinject
@observer
export class DisplayGridNav extends React.Component<IDisplayGridNav> {
    public render() {
        return (
            <>
                <SubTopBar className="d-block">
                    <DisplayFilter
                        vm={this.props.vm}
                        hideInput={this.hideInput}
                        hideClearSort
                        renderAfter={
                            <div className="ms-auto flex-items-center">
                                <div
                                    className={`extra-filters ${
                                        !this.appStore.isMobile || this.showExtraFilters ? 'show' : ''
                                    }`}
                                >
                                    {this.props.vm.gridViewType == DisplayGridViewTypes.Grid &&
                                        <>
                                            <div className="grid-column-search mx-1 rounded d-flex" style={{minWidth: 300}}>
                                                <Select
                                                    placeholder="Find column..."
                                                    options={this.gridOptions}
                                                    isClearable={!this.appStore.isMobile}
                                                    onChange={this.onFindChange}
                                                    className='w-100 react-select-container'
                                                    classNamePrefix='react-select'
                                                />
                                            </div>

                                            <Button className="btn btn-secondary me-1" onClick={this.toggleColumns}>
                                                <BsLayoutThreeColumns />
                                            </Button>

                                            <DisplayGridColumnPresets vm={this.props.vm} className="me-1"/>
                                            
                                            <Button className="btn btn-secondary f-18 py-1 px-2" onClick={this.toggleBulkActionsMenu}>
                                                <BsCardChecklist />
                                            </Button>
                                        </>
                                    }

                                    {this.props.vm.gridViewType == DisplayGridViewTypes.Grid &&
                                        <Button className="btn btn-secondary ms-1" onClick={this.openSettingsPopout}>
                                            <BsGear/>
                                        </Button>
                                    }
                                    
                                    <DisplayGridViewSelector vm={this.props.vm} className="mx-1"/>
                                </div>

                                {this.appStore.isMobile && (
                                    <a onClick={this.toggleExtraFilters} className={this.filterToggleClass}>
                                        <BsFillFunnelFill />
                                    </a>
                                )}
                                {this.renderSettings &&
                                    <GridSettings className="me-1" popoutHandle={this.popoutHandle} vm={this.props.vm}/>
                                }
                            </div>
                        }
                    />
                </SubTopBar>
                {this.appStore.storage.showBulkActionsMenu &&
                    <SubTopBar>
                        <GridBulkActions vm={this.props.vm} className="h-100"/>
                    </SubTopBar>
                }
            </>
        );
    }
    
    @bind
    @action
    private toggleColumns(){
        let isOpen = this.appStore.gridRef?.api.getOpenedToolPanel();
        
        if(isOpen) {
            this.appStore.gridRef?.api.closeToolPanel();
        } else {
            this.appStore.gridRef?.api.openToolPanel('columns');
        }
    }
    
    @bind
    @action
    private openSettingsPopout() {
        this.renderSettings = true;
        this.popoutHandle.open();
    }
    
    @bind
    @action
    private toggleBulkActionsMenu() {
        this.appStore.storage.showBulkActionsMenu = !this.appStore.storage.showBulkActionsMenu;
        if(this.appStore.storage.showBulkActionsMenu) {
            this.appStore.gridRef?.api.setGridOption(
                "rowSelection",
                {
                    mode: "multiRow",
                    selectAll: "all",
                }
            );
           
        } else {
            this.appStore.gridRef?.api.setGridOption(
                "rowSelection",
                undefined
            );
        }
    }
    
    @bind
    @action
    private refreshGrid(){
        this.appStore.gridRef?.api.refreshInfiniteCache();
    }

    @bind
    @action
    private toggleExtraFilters() {
        this.showExtraFilters = !this.showExtraFilters;
    }

    @bind
    @action
    private onFindChange(columnOption: { value: string; label: string; } | null ) {
        if(this.currentSearchColumn) {
            let colDef = this.currentSearchColumn.getColDef();
            if(colDef){
                colDef.headerClass = "";
                colDef.cellClass = "";
            }

            this.currentSearchColumn = undefined;
        }
        let currentColumns = this.appStore.gridRef?.api.getColumnState();

        if(columnOption) {
           this.currentSearchColumn  = this.appStore.gridRef?.api.getColumn(columnOption.value) || undefined;
           
            if (this.currentSearchColumn) {
                this.appStore.gridRef?.api.ensureColumnVisible(this.currentSearchColumn.getColId(), 'middle');
                let colDef = this.currentSearchColumn?.getColDef();
                if(colDef){
                    colDef.headerClass = "header-search";
                    colDef.cellClass = "cell-search";
                }
            }
        }

        if (currentColumns) {
            this.accountStore.tabStorage.activeGridColumns = currentColumns;
        }

        let columnDefs = this.appStore.gridRef?.api.getColumnDefs();
        this.appStore.gridRef?.api.setGridOption("columnDefs", columnDefs);
    }

    @computed
    private get hideInput() {
        const {hideInput} = this.props;

        if (hideInput) return true;
        else if (!this.appStore.isMobile) return false;
        else return this.showExtraFilters;
    }

    @computed
    private get filterToggleClass() {
        let toggleClass = 'btn btn-secondary btn-sm ms-1';
        if (this.showExtraFilters) toggleClass = toggleClass + ' active';
        return toggleClass;
    }
    
   
    @computed
    private get gridOptions() {
        let columns = this.props.vm.searchableGridColumns;

        return columns.map(c => {
            return {
                value: c.colId,
                label: c.headerName,
            };
        });
    }

    private popoutHandle = new PopoutHandle();
    @observable
    private renderSettings = false;
    @observable
    private showExtraFilters: boolean = false;
    @observable
    private currentSearchColumn?: Column;
    @inject
    private accountStore!: AccountStore;
    @inject
    private appStore!: AppStore;
}
